// Relevance ranking for the Context Pack Generator (ARCHITECTURE.md §9.2).
//
// Given the files reached during impact analysis, the ranker assigns each a
// relevance score in [0, 1] combining four weighted factors:
//
//   impact depth      (0.4) — closer to the change is more relevant
//   artifact membership (0.2) — files inside an artifact score a bonus
//   edge connectivity (0.2) — more graph edges = more central
//   evidence relevance (0.2) — strong, diverse evidence beats weak signals
//
// The evidence-relevance factor is the deterministic, evidence-type-derived
// relevance score (Issue #21) — not the per-edge confidence value, which is now
// telemetry metadata. The result is a list of RankedFile sorted by score
// descending; ties break on shallower depth first, then path for stability.

import { computeRelevanceScore } from "../evidence/index.js";
import type { Evidence, EvidenceType } from "../evidence/types.js";
import type { AffectedEntry } from "../impact/types.js";

// ---------------------------------------------------------------------------
// RankedFile
// ---------------------------------------------------------------------------

export interface RankedFile {
  path: string;
  artifactId?: string;
  /** Combined relevance score, in the range 0.0 – 1.0. */
  relevanceScore: number;
  /** BFS distance from the change set (0 for directly changed files). */
  depth: number;
  evidence: Evidence[];
}

// ---------------------------------------------------------------------------
// Scoring weights (§9.2)
// ---------------------------------------------------------------------------

export const RANK_WEIGHTS = {
  depth: 0.4,
  artifact: 0.2,
  edges: 0.2,
  relevance: 0.2,
} as const;

export interface RankFilesOptions {
  affectedEntries: AffectedEntry[];
  /** filePath → artifact IDs. Membership contributes the artifact bonus. */
  artifactMapping: Map<string, string[]>;
  /** filePath → edge count (graph degree). Normalized against the max. */
  edgeCounts?: Map<string, number>;
  /** Evidence types to exclude when deriving the relevance factor (Issue #21). */
  evidenceExclude?: EvidenceType[];
}

/**
 * The evidence-derived relevance of an affected entry, clamped to [0, 1] for
 * the weighted sum. Prefers the entry's precomputed `relevanceScore`; falls
 * back to deriving it from the path evidence. Entries with no evidence (e.g.
 * the directly-changed files) are treated as fully relevant.
 */
function entryRelevance(
  entry: AffectedEntry,
  exclude?: EvidenceType[],
): number {
  const score =
    entry.relevanceScore ??
    (entry.evidence.length === 0
      ? 1
      : computeRelevanceScore(entry.evidence, exclude ? { exclude } : {}));
  return Math.min(1, score);
}

/**
 * Rank the affected files by relevance (ARCHITECTURE.md §9.2). Returns a list
 * sorted by relevanceScore descending.
 */
export function rankFiles(options: RankFilesOptions): RankedFile[] {
  const { affectedEntries, artifactMapping, edgeCounts, evidenceExclude } =
    options;

  // Normalize edge counts against the busiest file so the factor stays in
  // [0, 1]; with no counts the connectivity factor contributes nothing.
  let maxEdges = 0;
  if (edgeCounts) {
    for (const count of edgeCounts.values()) {
      if (count > maxEdges) maxEdges = count;
    }
  }

  const ranked: RankedFile[] = affectedEntries.map((entry) => {
    const depthScore = 1 / (1 + entry.depth);

    const artifactIds = artifactMapping.get(entry.path);
    const inArtifact = artifactIds !== undefined && artifactIds.length > 0;
    const artifactScore = inArtifact ? 1 : 0;

    const edgeCount = edgeCounts?.get(entry.path) ?? 0;
    const edgeScore = maxEdges > 0 ? edgeCount / maxEdges : 0;

    const relevanceFactor = entryRelevance(entry, evidenceExclude);

    const relevanceScore =
      RANK_WEIGHTS.depth * depthScore +
      RANK_WEIGHTS.artifact * artifactScore +
      RANK_WEIGHTS.edges * edgeScore +
      RANK_WEIGHTS.relevance * relevanceFactor;

    return {
      path: entry.path,
      ...(inArtifact ? { artifactId: artifactIds[0] } : entry.artifactId
        ? { artifactId: entry.artifactId }
        : {}),
      relevanceScore,
      depth: entry.depth,
      evidence: entry.evidence,
    };
  });

  ranked.sort(
    (a, b) =>
      b.relevanceScore - a.relevanceScore ||
      a.depth - b.depth ||
      a.path.localeCompare(b.path),
  );

  return ranked;
}
